<template>
<v-container class="pa-4">
  <v-row class="mb-4 align-center px-4 py-3 bg-light border-bottom rounded-t">
    <div class="d-flex align-center">
      <v-icon small class="mr-2" color="blue darken-2">fas fa-cogs</v-icon>
      <span class="text-subtitle-2 font-weight-bold">Procesos de Producción</span>
    </div>
    <v-spacer></v-spacer>
    <div v-if="!readonly">
      <v-btn 
        small 
        color="primary" 
        outlined 
        depressed 
        @click="addingProcess=true" 
        :disabled='disabled'
        class="mr-2"
      >
        <v-icon x-small class="mr-1">fas fa-plus</v-icon>
        Proceso
      </v-btn>
      <v-btn 
        small 
        color="primary" 
        outlined 
        depressed 
        @click="addingCustom=true" 
        :disabled='disabled'
      >
        <v-icon x-small class="mr-1">fas fa-magic</v-icon>
        Personalizado
      </v-btn>
    </div>
  </v-row>

  <v-simple-table v-if="items.length > 0" class="modern-table border rounded overflow-hidden">
    <template v-slot:default>
      <thead>
        <tr>
          <th width="40" class="text-center font-weight-bold grey--text">#</th>
          <th class="text-left font-weight-bold">Descripción</th>
          <th width="100" class="text-center font-weight-bold">Cant. PP</th>
          <th class="text-right font-weight-bold" v-if="$can('readCost', 'SalesNote')">Costo Unit.</th>
          <th width="100" class="text-left font-weight-bold">Unidad</th>
          <th class="text-right font-weight-bold" v-if="$can('readCost', 'SalesNote')">Costo Total</th>
          <th v-if="!readonly" width="60" class="text-center font-weight-bold"></th>
        </tr>
      </thead>

      <draggable 
        :list="items"  
        draggable=".recipe-row-move" 
        tag="tbody"
        :disabled="readonly"
      >
        <tr
          v-for="(item, index) in items"
          :key="index"
          :class="readonly ? '' : 'recipe-row-move cursor-move'"
        >
          <td class="text-center caption grey--text">
            <v-icon v-if="!readonly" x-small class="mr-1 grey--text text--lighten-1">fas fa-grip-lines</v-icon>
            {{ index + 1 }}
          </td>
          <td>
            <div class="body-2 font-weight-medium py-2 text-left">{{item.name}}</div>
          </td>
          <td class="px-2">
            <div class="d-flex flex-column align-center">
              <v-text-field
                v-if="!readonly"
                v-model="item.qty"
                dense
                outlined
                hide-details
                type="number"
                class="text-right centered-input"
                :disabled="disabled"
                style="max-width: 80px"
              ></v-text-field>
              <div v-else class="font-weight-bold">{{item.qty}}</div>
              <v-chip v-if="item.merma > 0" x-small dark color="orange darken-1" class="mt-1">
                + {{item.merma}} merma
              </v-chip>
            </div>
          </td>
          <td class="text-right" v-if="$can('readCost', 'SalesNote')">
            <span class="body-2">{{item.price | moneda}}</span>
          </td>
          <td>
            <v-chip x-small label outlined color="grey darken-1">{{item.unit_name}}</v-chip>
          </td>
          <td v-if="$can('readCost', 'SalesNote')" class="text-right">
            <div class="d-flex flex-column align-end">
              <span class="font-weight-bold">{{individualCost(item) | moneda}}</span>
              <v-chip v-if="minPriceFlag(item)" x-small dark color="blue darken-1" class="mt-1">min.</v-chip>
              <v-progress-linear 
                :value="(individualCost(item) / (total || 1)) * 100"
                color="blue darken-2"
                height="3"
                rounded
                class="mt-1"
                style="width: 60px"
              ></v-progress-linear>
            </div>
          </td>
          <td v-if="!readonly" class="text-center">
            <v-btn icon x-small color="error" @click="deleteItem(item)" :disabled='disabled'>
              <v-icon x-small>fas fa-trash-alt</v-icon>
            </v-btn>
          </td>
        </tr>
      </draggable>
    </template>
  </v-simple-table>

  <v-alert v-else type="info" text dense icon="fas fa-info-circle" class="mt-4 mx-4">
    No se han registrado procesos para este producto.
  </v-alert>

  <!-- Dialogs -->
  <v-dialog v-model="addingProcess" max-width="800px">
    <v-card class="pa-0 modern-card overflow-hidden">
      <v-sheet color="primary" class="pa-6 d-flex align-center">
        <v-avatar size="44" color="white opacity-20" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-cogs</v-icon>
        </v-avatar>
        <div>
          <div class="caption white--text opacity-80 font-weight-black text-uppercase tracking-widest mb-1">Catálogo de Operaciones</div>
          <div class="text-h5 white--text font-weight-black">Agregar Proceso</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon color="white" large @click="addingProcess = false">
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-sheet>
      <div class="pa-6">
        <add-cost @add="add" :is_supply="false"/>
      </div>
    </v-card>
  </v-dialog>

  <v-dialog v-model="addingCustom" max-width="600px">
    <cost-create-update :cost="newCost" @save="add" @close="addingCustom=false" :custom="true" /> 
  </v-dialog>
</v-container>
</template>

<script>
import AddCost from '@/components/products/costs/AddCost'
import CostCreateUpdate from '@/components/products/costs/CostCreateUpdate'
import draggable from 'vuedraggable'

export default {
  name: 'Recipe',
  components: { AddCost, CostCreateUpdate, draggable },
  props: ['items', 'mts2', 'qty', 'total', 'readonly', 'disabled'],
  data: () => ({
    addingProcess: false,
    addingCustom: false,
    newCost: {
      id: -1, unit: '', name: '', price: 0, qty: 1, 
      min_price: 0, group: -1, is_supply: false
    },
  }),
  methods: {
    add(item) {
      this.items.push(item)
      this.resetModalState()
    },
    resetModalState() {
      this.addingCustom = false
      this.addingProcess = false
      this.newCost = {
        id: -1, unit: '', name: '', price: 0, qty: 1, 
        min_price: 0, group: -1, is_supply: false
      }
    },
    deleteItem(item) {
      const index = this.items.indexOf(item)
      this.items.splice(index, 1)
    },
    individualCost(item) {
      const costUNIT = item.price * (this.qty * item.qty + item.merma)
      const costMTS2 = item.price * this.mts2 * item.qty
      const costFIJO = item.price * item.qty
      
      let baseCost = 0
      switch(item.unit) {
        case 1: baseCost = costMTS2; break
        case 2: baseCost = costUNIT; break
        default: baseCost = costFIJO
      }
      return baseCost > item.min_price ? baseCost : item.min_price
    },
    minPriceFlag(item) {
      return item.min_price > this.individualCost(item)
    }
  }
}
</script>

<style scoped>
.bg-light { background-color: #f8fbfc; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06); }
.border { border: 1px solid rgba(0,0,0,0.08) !important; }
.cursor-move { cursor: move; }
.centered-input >>> input { text-align: right; }

.modern-table >>> td { height: 56px !important; border-bottom: 1px solid rgba(0,0,0,0.03) !important; font-size: 0.85rem !important; }
.modern-table >>> th { text-transform: uppercase; font-size: 0.65rem !important; font-weight: 800 !important; color: #64748b !important; letter-spacing: 1px !important; background-color: #f8fbfc !important; }
.tracking-widest { letter-spacing: 2px; }
.opacity-80 { opacity: 0.8; }
</style>
